/**
 * POD Dashboard — landing page after a POD subscriber logs in.
 * Surfaces ONLY POD-specific data. No LMS programmes, no LMS courses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PodLayout from "../components/PodLayout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Megaphone, Files, Sparkle, UsersThree, ArrowRight, Lightning, ChatCircle } from "@phosphor-icons/react";

export default function PodDashboardPage() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState({ webinars: 0, resources: 0, templates: 0, roundtable: "" });

  useEffect(() => {
    api.get("/pod/me").then(r => setMe(r.data)).catch(() => {});
    api.get("/pod/dashboard-stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "Upcoming Webinars", value: stats.webinars, icon: Megaphone, color: "bg-amber-400 text-slate-950", href: "/pod/webinars" },
    { label: "Resources", value: stats.resources, icon: Files, color: "bg-blue-800 text-white", href: "/pod/resources" },
    { label: "AI Templates", value: stats.templates, icon: Sparkle, color: "bg-purple-700 text-white", href: "/pod/ai-templates" },
    { label: "My Roundtable", value: stats.roundtable || "Awaiting assignment", icon: UsersThree, color: "bg-emerald-700 text-white", href: "/pod/roundtable" },
  ];

  return (
    <PodLayout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="pod-dashboard-page">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">SBL Leadership POD</p>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome back, {user?.name?.split(" ")[0] || "Member"}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Your home for high-impact webinars, peer roundtables, ready-to-use AI templates and curated leadership resources.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {cards.map(c => {
            const Icon = c.icon;
            return (
              <Link key={c.label} to={c.href} className="block group" data-testid={`pod-stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Card className="border border-slate-200 rounded-none shadow-sm hover:shadow-md hover:border-slate-400 transition-all h-full">
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 ${c.color} flex items-center justify-center mb-3`}><Icon size={20} weight="bold" /></div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{c.label}</p>
                    <p className="text-2xl font-extrabold text-slate-950 mt-1 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.value}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 border border-slate-200 rounded-none">
            <CardContent className="p-6">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600 mb-2">This Week</p>
              <h2 className="text-xl font-bold text-slate-950 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Quick actions</h2>
              <p className="text-sm text-slate-600 mb-5">Jump straight to what you need.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/pod/ai-templates"><Button variant="outline" className="w-full rounded-none justify-start gap-2 h-12 border-slate-300" data-testid="pod-action-ai"><Lightning size={16} weight="fill" className="text-amber-500" /> Generate a document with AI</Button></Link>
                <Link to="/pod/webinars"><Button variant="outline" className="w-full rounded-none justify-start gap-2 h-12 border-slate-300" data-testid="pod-action-webinar"><Megaphone size={16} weight="fill" className="text-blue-800" /> Register for a webinar</Button></Link>
                <Link to="/pod/roundtable"><Button variant="outline" className="w-full rounded-none justify-start gap-2 h-12 border-slate-300" data-testid="pod-action-roundtable"><UsersThree size={16} weight="fill" className="text-emerald-700" /> Open my Roundtable</Button></Link>
                <Link to="/pod/forum"><Button variant="outline" className="w-full rounded-none justify-start gap-2 h-12 border-slate-300" data-testid="pod-action-forum"><ChatCircle size={16} weight="fill" className="text-purple-700" /> POD Forum</Button></Link>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-amber-200 rounded-none bg-amber-50/30">
            <CardContent className="p-6">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-2">POD Membership</p>
              <h3 className="text-lg font-bold text-slate-950 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{me?.tier_label || me?.tier || "Active member"}</h3>
              <p className="text-xs text-slate-600 mb-4">{me?.renewal_date ? `Renews on ${new Date(me.renewal_date).toLocaleDateString()}` : "Lifetime membership benefits while active."}</p>
              <Link to="/pod/profile">
                <Button className="w-full bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2 text-sm" data-testid="pod-manage-membership">Manage subscription <ArrowRight size={14} weight="bold" /></Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </PodLayout>
  );
}
